import { CaretLeft, CaretRight, NavigationArrow, Plus } from "@phosphor-icons/react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KIND_STYLE, STAGES } from "@/lib/calendarConstants";

export function CalendarToolbar({
  view,
  setView,
  conf,
  setConf,
  confirmedCount,
  unconfirmedCount,
  stageFilter,
  setStageFilter,
  hideCompleted,
  setHideCompleted,
  cutoff,
  setCutoff,
  showArchived,
  setShowArchived,
  showConflicts,
  onToggleConflicts,
  onAddEvent,
  onPlanRoutes,
  onPrev,
  onToday,
  onNext,
  cursor,
  range,
  eventsLength,
}) {
  const rangeText =
    view === "day"
      ? format(cursor, "EEEE, MMM d yyyy")
      : view === "week"
        ? `${format(range.from, "MMM d")} → ${format(range.to, "MMM d yyyy")}`
        : format(cursor, "MMMM yyyy");

  return (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
      data-testid="calendar-header-card"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500">Schedule</div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Calendar</h1>
          <p className="mt-1 text-sm text-zinc-500" data-testid="calendar-range">
            {rangeText} · {eventsLength} scheduled · {confirmedCount} confirmed
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-zinc-800">
            {["month", "week", "day"].map((v) => (
              <button
                key={v}
                data-testid={`calendar-view-${v}`}
                onClick={() => setView(v)}
                className={`h-11 px-4 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                  view === v
                    ? "bg-indigo-500 text-white"
                    : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="flex overflow-hidden rounded-md border border-zinc-800">
            {[
              ["all", "All", null],
              ["confirmed", "Confirmed", confirmedCount],
              ["unconfirmed", "Unconfirmed", unconfirmedCount],
            ].map(([v, lbl, n]) => (
              <button
                key={v}
                data-testid={`calendar-confirm-${v}`}
                onClick={() => setConf(v)}
                className={`h-11 px-3 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                  conf === v
                    ? "bg-emerald-500 text-white"
                    : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                {lbl}
                {n != null ? ` ${n}` : ""}
              </button>
            ))}
          </div>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger
              data-testid="calendar-stage-filter"
              className="h-11 w-[150px] border-zinc-800 bg-zinc-900 text-xs font-semibold uppercase tracking-widest text-zinc-300"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All stages</SelectItem>
              {STAGES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="calendar-hide-completed"
            variant="outline"
            onClick={() => setHideCompleted((s) => !s)}
            title="Hide completed projects from the calendar"
            className={`h-11 px-3 text-xs font-semibold uppercase tracking-widest ${
              hideCompleted
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
            }`}
          >
            {hideCompleted ? "Completed hidden" : "Hide completed"}
          </Button>
          <div className="flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-2 h-11">
            <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">
              Cutoff
            </span>
            <input
              type="time"
              value={cutoff}
              onChange={(e) => setCutoff(e.target.value)}
              data-testid="calendar-cutoff"
              title="Flag days where a crew runs past this time"
              className="bg-transparent text-sm text-zinc-100 focus:outline-none"
            />
          </div>
          <Button
            data-testid="calendar-toggle-archived"
            variant="outline"
            onClick={() => setShowArchived((s) => !s)}
            title="Show archived/closed jobs for looking back at past work"
            className={`h-11 px-3 text-xs font-semibold uppercase tracking-widest ${
              showArchived
                ? "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
            }`}
          >
            {showArchived ? "Archived shown" : "Show archived"}
          </Button>
          <Button
            data-testid="calendar-add-event"
            onClick={onAddEvent}
            className="h-11 bg-indigo-500 px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            <Plus size={16} weight="bold" className="mr-1.5" /> Add event
          </Button>
          <Button
            data-testid="calendar-toggle-conflicts"
            variant="outline"
            onClick={onToggleConflicts}
            title="Show or hide crew double-booking (overbooked) alerts"
            className={`h-11 px-3 text-xs font-semibold uppercase tracking-widest ${
              showConflicts
                ? "border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20"
                : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
            }`}
          >
            {showConflicts ? "Alerts on" : "Alerts off"}
          </Button>
          <Button
            data-testid="calendar-route-planner"
            variant="outline"
            onClick={onPlanRoutes}
            className="h-11 border-indigo-500/40 bg-indigo-500/10 px-3 text-xs font-semibold uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/20"
          >
            <NavigationArrow size={16} weight="bold" className="mr-1.5" />
            Plan routes
          </Button>
          <Button
            data-testid="calendar-prev"
            variant="outline"
            onClick={onPrev}
            className="h-11 border-zinc-800 bg-zinc-900 px-3"
          >
            <CaretLeft size={16} weight="bold" />
          </Button>
          <Button
            data-testid="calendar-today"
            variant="outline"
            onClick={onToday}
            className="h-11 border-zinc-800 bg-zinc-900 text-xs font-semibold uppercase tracking-widest"
          >
            Today
          </Button>
          <Button
            data-testid="calendar-next"
            variant="outline"
            onClick={onNext}
            className="h-11 border-zinc-800 bg-zinc-900 px-3"
          >
            <CaretRight size={16} weight="bold" />
          </Button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3 border-t border-zinc-800 pt-4 text-[11px] uppercase tracking-widest text-zinc-500">
        {Object.keys(KIND_STYLE).map((k) => (
          <span key={k} className="inline-flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full border ${KIND_STYLE[k]}`} /> {k}
          </span>
        ))}
      </div>
    </div>
  );
}
